import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Rating,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi, reviewApi } from '../services/api';
import { ReviewFormData } from '../types';
import { AddressSelector } from '../components/AddressSelector';
import { GAEvents } from '../hooks/useGoogleAnalytics';

const reviewCategories = [
  { key: 'childcare_education', label: '子育て・教育', description: '保育園、学校、公園など' },
  { key: 'medical_welfare', label: '医療・福祉', description: '病院、介護施設など' },
  { key: 'transportation', label: '交通・アクセス', description: '電車、バス、道路状況など' },
  { key: 'shopping_commercial', label: '商業・買い物', description: 'スーパー、商店街など' },
  { key: 'living_environment', label: '住環境', description: '治安、騒音、自然環境など' },
  { key: 'government_services', label: '行政サービス', description: '窓口対応、手続きの便利さなど' }
];

const steps = ['住所選択', '評価・コメント入力', '確認・投稿'];

export const CreateReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Step 1: 住所選択
  const [selectedAddress, setSelectedAddress] = useState<{
    prefecture: string;
    city: string; 
    prefectureCode: string;
    cityCode: string;
  } | null>(null);

  // プロフィールページからの事前入力データを処理
  useEffect(() => {
    const state = location.state as { prefillAddress?: any } | null;
    console.log('CreateReviewPage location state:', state);
    
    if (state?.prefillAddress) {
      const prefillData = state.prefillAddress;
      console.log('Prefill data:', prefillData);
      
      // 住所文字列から都道府県と市区町村を分離
      const addressParts = prefillData.address ? prefillData.address.split(/[都道府県市区町村]/) : [];
      const prefecture = addressParts[0] ? addressParts[0] + (prefillData.address.includes('都') ? '都' : prefillData.address.includes('道') ? '道' : prefillData.address.includes('府') ? '府' : '県') : '';
      const city = addressParts[1] ? addressParts[1] + (prefillData.address.includes('市') ? '市' : prefillData.address.includes('区') ? '区' : prefillData.address.includes('町') ? '町' : prefillData.address.includes('村') ? '村' : '') : '';
      
      setSelectedAddress({
        prefecture: prefecture,
        city: city,
        prefectureCode: prefillData.prefecture_code || '',
        cityCode: prefillData.city_code || '',
      });
      
      // フォームデータも更新
      setFormData(prev => ({
        ...prev,
        prefecture_code: prefillData.prefecture_code || '',
        city_code: prefillData.city_code || '',
        address: prefillData.address || ''
      }));
      
      // 住所が事前入力されている場合は次のステップに進む
      setActiveStep(1);
    }
  }, [location.state]);

  // Step 2: 評価・コメント
  const [formData, setFormData] = useState<ReviewFormData>({
    prefecture_code: '',
    city_code: '',
    address: '',
    ratings: {
      childcare_education: 3,
      medical_welfare: 3,
      transportation: 3,
      shopping_commercial: 3,
      living_environment: 3,
      government_services: 3
    },
    comments: {
      childcare_education: '',
      medical_welfare: '',
      transportation: '',
      shopping_commercial: '',
      living_environment: '',
      government_services: ''
    }
  });

  // 住所選択
  const handleAddressSelect = (address: {
    prefecture: string;
    city: string;
    prefectureCode: string;
    cityCode: string;
  }) => {
    setSelectedAddress(address);
    setFormData(prev => ({
      ...prev,
      prefecture_code: address.prefectureCode,
      city_code: address.cityCode,
      address: `${address.prefecture} ${address.city}`
    }));
  };

  // 評価変更
  const handleRatingChange = (category: string, value: number | null) => {
    if (value !== null) {
      setFormData(prev => ({
        ...prev,
        ratings: {
          ...prev.ratings,
          [category]: value
        }
      }));
    }
  };

  // コメント変更
  const handleCommentChange = (category: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      comments: {
        ...prev.comments,
        [category]: value
      }
    }));
  };

  // 次のステップ
  const handleNext = () => {
    if (activeStep === 0) {
      if (!selectedAddress) {
        setError('住所を選択してください');
        return;
      }
    } else if (activeStep === 1) {
      // 全てのコメントが入力されているかチェック
      const emptyComments = reviewCategories.filter(cat => 
        !formData.comments[cat.key as keyof typeof formData.comments].trim()
      );
      if (emptyComments.length > 0) {
        setError(`${emptyComments.map(cat => cat.label).join('、')}のコメントを入力してください`);
        return;
      }
    }
    
    setError('');
    setActiveStep(prev => prev + 1);
  };

  // 前のステップ
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError('');
  };

  // 口コミ投稿
  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      await reviewApi.createReview(formData);
      
      // Google Analytics: 口コミ投稿成功イベントを送信
      GAEvents.reviewSubmit();
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/reviews');
      }, 2000);
    } catch (err: any) {
      console.error('Review submission error:', err);
      
      // Google Analytics: 口コミ投稿エラーイベントを送信
      GAEvents.error('review_submit_failed', err.response?.data?.error || 'Unknown error');
      
      setError(err.response?.data?.error || '口コミ投稿でエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="md">
        <Box sx={{ marginTop: 8, textAlign: 'center' }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            口コミが投稿されました！他の方の口コミも閲覧できるようになりました。
          </Alert>
          <Typography variant="h6">
            口コミ一覧ページに移動します...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 4 }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            口コミを投稿
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step 1: 住所選択 */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                住所を選択してください
              </Typography>
              
              <AddressSelector
                onAddressSelect={handleAddressSelect}
                selectedAddress={selectedAddress}
                compact={true}
              />
            </Box>
          )}

          {/* Step 2: 評価・コメント入力 */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                各項目の評価とコメントを入力してください
              </Typography>
              
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2">選択した住所:</Typography>
                <Typography variant="body1">{formData.address}</Typography>
              </Box>

              {reviewCategories.map((category) => (
                <Card key={category.key} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {category.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {category.description}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography component="legend">評価:</Typography>
                      <Rating
                        value={formData.ratings[category.key as keyof typeof formData.ratings]}
                        onChange={(_, value) => handleRatingChange(category.key, value)}
                        size="large"
                      />
                    </Box>
                    
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={`${category.label}についてのコメント`}
                      placeholder={`${category.label}について具体的な体験や感想を教えてください...`}
                      value={formData.comments[category.key as keyof typeof formData.comments]}
                      onChange={(e) => handleCommentChange(category.key, e.target.value)}
                      required
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Step 3: 確認・投稿 */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                投稿内容の確認
              </Typography>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>住所:</strong> {formData.address}
                  </Typography>
                </CardContent>
              </Card>

              {reviewCategories.map((category) => (
                <Card key={category.key} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1">{category.label}</Typography>
                      <Chip 
                        label={`${formData.ratings[category.key as keyof typeof formData.ratings]}★`} 
                        color="primary" 
                      />
                    </Box>
                    <Typography variant="body2">
                      {formData.comments[category.key as keyof typeof formData.comments]}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* ナビゲーションボタン */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              戻る
            </Button>
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : '投稿する'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                次へ
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default CreateReviewPage;
